import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Trash2, Search, X } from "lucide-react";

export default function AdminNorms() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [norms, setNorms] = useState([]);
  const [query, setQuery] = useState("");
  const [stageType, setStageType] = useState("all");

  const load = () => api.get("/norms").then((r) => setNorms(r.data));
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return norms.filter((n) => {
      if (stageType !== "all" && n.stage_type !== stageType) return false;
      if (!q) return true;
      return (
        (n.table_code || "").toLowerCase().includes(q) ||
        (n.description || "").toLowerCase().includes(q)
      );
    });
  }, [norms, query, stageType]);

  if (user?.role !== "superadmin") {
    return (
      <div className="text-slate-500">
        Yalnız Super Admin bu səhifəyə daxil ola bilər.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="label-overline mb-1">{t("norms")}</div>
        <h1 className="font-heading font-black text-4xl tracking-tight">
          DESN Normaları
        </h1>
        <p className="text-slate-600 mt-2">
          DESN-2021-27 əsasında seed edilmiş norma cədvəli.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cədvəl kodu və ya təsvir..."
            className="pl-9 pr-9 bg-white"
            data-testid="norms-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              data-testid="norms-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={stageType} onValueChange={setStageType}>
          <SelectTrigger className="w-[220px] bg-white" data-testid="norms-stage-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Bütün mərhələ tipləri</SelectItem>
            <SelectItem value="site_cleaning">{t("site_cleaning")}</SelectItem>
            <SelectItem value="fill_layers">{t("fill_layers")}</SelectItem>
            <SelectItem value="road_base">{t("road_base")}</SelectItem>
            <SelectItem value="asphalt_coating">{t("asphalt_coating")}</SelectItem>
          </SelectContent>
        </Select>
        <span className="mono text-xs text-slate-500" data-testid="norms-count">
          {filtered.length} / {norms.length} norma
        </span>
      </div>

      <div className="space-y-4">
        {filtered.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-500">
            {norms.length === 0 ? "Norma yoxdur" : "Axtarışa uyğun norma tapılmadı"}
          </div>
        )}
        {filtered.map((n) => (
          <div
            key={n.id}
            className="bg-white border border-slate-200 rounded-lg p-5"
            data-testid={`norm-${n.table_code}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="mono text-xs text-slate-400">
                  {n.table_code}
                </div>
                <h3 className="font-heading font-bold text-lg">
                  {n.description}
                </h3>
              </div>
              <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded">
                {t(n.stage_type)}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
              <div>
                <div className="label-overline">{t("base_unit")}</div>
                <div className="mono font-bold">{n.base_unit}</div>
              </div>
              <div>
                <div className="label-overline">Baza dəyəri</div>
                <div className="mono font-bold">{n.base_value}</div>
              </div>
              <div>
                <div className="label-overline">{t("base_thickness")}</div>
                <div className="mono font-bold">{n.base_thickness ?? "—"} sm</div>
              </div>
              <div>
                <div className="label-overline">{t("additional_norm")}</div>
                <div className="mono font-bold">{n.additional_norm ?? "—"}</div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 text-xs">
              {[
                ["workers", t("workers")],
                ["machinery", t("machinery")],
                ["materials", t("materials")],
              ].map(([k, label]) => (
                <div key={k} className="bg-slate-50 rounded p-3">
                  <div className="label-overline mb-2">{label}</div>
                  <div className="space-y-1">
                    {(n.resources?.[k] || []).map((r, i) => (
                      <div key={i} className="flex justify-between mono">
                        <span className="truncate">{r.name}</span>
                        <span>
                          {r.norm} {r.unit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
